import { css, Global } from '@emotion/react';

import { mkAssetUrl } from '@lib/utils';
import { defaultTheme } from '@lib/themes';

import type React from 'react';

export const GlobalStyles: React.FC = () => {
  return (
    <Global
      styles={css`
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(${mkAssetUrl('/static/fonts/Inter-Regular.otf')}) format('opentype');
        }
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 500;
          font-display: swap;
          src: url(${mkAssetUrl('/static/fonts/inter-v2-latin-ext_latin-500.woff')}) format('woff');
        }
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url(${mkAssetUrl('/static/fonts/Inter-Bold.otf')}) format('opentype');
        }
        @font-face {
          font-family: 'FrankRuhlLibre';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url(${mkAssetUrl('/static/fonts/FrankRuhlLibre-Regular.ttf')}) format('truetype');
        }
        @font-face {
          font-family: 'FrankRuhlLibre';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url(${mkAssetUrl('/static/fonts/FrankRuhlLibre-Bold.ttf')}) format('truetype');
        }

        * {
          text-rendering: optimizeLegibility;
          -webkit-font-smoothing: antialiased;
          box-sizing: border-box;
        }

        :root {
          --header__height: 60px;
          @media (min-width: ${defaultTheme.breakpoints.tablet_landscape}) {
            --header__height: 74px;
          }
          @media (min-width: ${defaultTheme.breakpoints.desktop_large}) {
            --header__height: 94px;
          }
        }

        body,
        html,
        input,
        ol,
        ul,
        a,
        p {
          margin: 0;
          padding: 0;
          font-family: Inter, sans-serif;
          color: ${defaultTheme.colors.primary};
        }

        /** hide scrollbar for IE-11 **/

        body {
          -ms-overflow-y: hidden;
        }

        .show-widget {
          overflow: hidden;
        }

        img {
          max-width: 100%;
        }

        a {
          text-decoration: none;
        }

        input {
          border: none;
        }

        .hidden {
          display: none !important;
        }

        #nprogress {
          pointer-events: none;
        }

        #nprogress .bar {
          background: ${defaultTheme.colors.primary};
          position: fixed;
          z-index: 1031;
          top: 0;
          left: 0;
          width: 100%;
          height: 3px;
        }

        /* Fancy blur effect */

        #nprogress .peg {
          display: block;
          position: absolute;
          right: 0;
          width: 100px;
          height: 100%;
          box-shadow: 0 0 10px #feb901, 0 0 5px #feb901;
          opacity: 1;
          -webkit-transform: rotate(3deg) translate(0px, -4px);
          -ms-transform: rotate(3deg) translate(0px, -4px);
          transform: rotate(3deg) translate(0px, -4px);
        }

        .mapboxgl-ctrl-bottom-right {
          @media (min-width: ${defaultTheme.breakpoints.tablet_landscape}) {
            bottom: 30px;
          }
        }

        .mapboxgl-ctrl-group {
          display: none;
          @media (min-width: ${defaultTheme.breakpoints.tablet_landscape}) {
            display: block;
          }
        }
        .mapboxgl-popup-content {
          border-radius: 0px;
          padding: ${defaultTheme.spacing * 2}px;
        }

        button.primary-flat {
          color: white;
          background: ${defaultTheme.colors.primary};
          border: none;
        }

        .mapboxgl-popup-close-button {
          border-radius: 0;
          color: #004b54;
          font-size: 36px;
          height: 24px;
          line-height: 11px;
          outline: none;
          padding: 0;
          right: 16px;
          top: 16px;
          width: 24px;
        }

        .mapboxgl-popup-close-button::after {
          content: '';
          display: inline-block;
        }

        /** style google auto-suggest box **/

        .pac-item {
          font-size: 14px;
          cursor: pointer;
          padding: 10px 10px 10px 40px;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          border: 0;

          span {
            line-height: 17px;
          }

          &:after {
            content: '';
            height: 1px;
            display: block;
            width: 100%;
            background: #e6e6e6;
            position: absolute;
            bottom: 0;
          }

          &:last-child:after {
            display: none;
          }

          &:hover {
            background-color: #f8f8f8;
          }
        }

        .pac-icon,
        .hdpi .pac-icon {
          width: 20px;
          height: 20px;
          margin-top: 0;
          background: url('https://images.ctfassets.net/64q0dihi81ut/6ugzLxS7WDvd56RrqCP8h/fa09a58917c87f53e496da4cc576bb4e/ic_location-grey.svg')
            no-repeat;
          vertical-align: middle;
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
        }

        .pac-logo:after,
        .hdpi.pac-logo:after {
          margin: 10px;
        }

        .show-on-desktop {
          display: none !important;
          @media (min-width: ${defaultTheme.breakpoints.tablet_landscape}) {
            display: block !important;
          }
        }

        .show-on-tablet {
          display: none !important;
          @media (min-width: ${defaultTheme.breakpoints.tablet}) {
            display: block !important;
          }
        }

        .show-on-mobile {
          display: block !important;
          @media (min-width: ${defaultTheme.breakpoints.tablet}) {
            display: none !important;
          }
        }

        .mapboxgl-popup {
          @media (max-width: ${defaultTheme.breakpoints.mobile_landscape}) {
            transform: translate(-102%, -135%) translate(100vw, 100vh) !important;
            width: 96vw;
            z-index: 10;

            .mapboxgl-popup-tip {
              display: none;
            }
          }

          .mapboxgl-popup-close-button {
            right: 5px;
            top: 10px;
          }
        }
      `}
    />
  );
};
